import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { ListenButton } from '../common/AudioGuide';
import HelpWithVideoTour from '../Help/HelpWithVideoTour';
import { isProposalScheduleAllowed } from '../../config/proposalScheduleAccess';

const API = '/proposal-schedules';
const ACCENT = '#0f766e';
const TABS = [
  { id: 'schedule', label: 'Schedule' },
  { id: 'docs', label: 'Source Docs' },
  { id: 'narrative', label: 'Narrative' },
  { id: 'exports', label: 'Exports' }
];

/** Lightweight markdown → safe HTML (headers, bold, italic, lists, code, paragraphs). */
function markdownToHtml(md) {
  if (!md) return '';
  const escape = (s) => String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  const lines = String(md).replace(/\r\n/g, '\n').split('\n');
  const out = [];
  let inUl = false;
  let inOl = false;

  const closeLists = () => {
    if (inUl) { out.push('</ul>'); inUl = false; }
    if (inOl) { out.push('</ol>'); inOl = false; }
  };

  const inline = (text) => {
    let t = escape(text);
    t = t.replace(/`([^`]+)`/g, '<code style="background:#f1f5f9;padding:1px 5px;border-radius:4px;font-size:0.9em">$1</code>');
    t = t.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    t = t.replace(/_([^_]+)_/g, '<em>$1</em>');
    return t;
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) {
      closeLists();
      continue;
    }
    if (/^###\s+/.test(line)) {
      closeLists();
      out.push(`<h3 style="margin:1.1em 0 .4em;font-size:1.05rem;color:#0f172a">${inline(line.replace(/^###\s+/, ''))}</h3>`);
      continue;
    }
    if (/^##\s+/.test(line)) {
      closeLists();
      out.push(`<h2 style="margin:1.25em 0 .45em;font-size:1.2rem;color:#0f172a;border-bottom:1px solid #e2e8f0;padding-bottom:4px">${inline(line.replace(/^##\s+/, ''))}</h2>`);
      continue;
    }
    if (/^#\s+/.test(line)) {
      closeLists();
      out.push(`<h1 style="margin:.2em 0 .6em;font-size:1.4rem;color:#0f172a">${inline(line.replace(/^#\s+/, ''))}</h1>`);
      continue;
    }
    if (/^[-*]\s+/.test(line)) {
      if (!inUl) { closeLists(); out.push('<ul style="margin:.4em 0 .8em;padding-left:1.4em">'); inUl = true; }
      out.push(`<li style="margin:.2em 0">${inline(line.replace(/^[-*]\s+/, ''))}</li>`);
      continue;
    }
    if (/^\d+\.\s+/.test(line)) {
      if (!inOl) { closeLists(); out.push('<ol style="margin:.4em 0 .8em;padding-left:1.4em">'); inOl = true; }
      out.push(`<li style="margin:.2em 0">${inline(line.replace(/^\d+\.\s+/, ''))}</li>`);
      continue;
    }
    closeLists();
    out.push(`<p style="margin:.45em 0;line-height:1.55;color:#334155">${inline(line)}</p>`);
  }
  closeLists();
  return out.join('');
}

/**
 * Estimation → Proposal Schedule
 * Auto-builds / revises draft for the header-selected estimate.
 */
const ProposalSchedules = ({ estimate = null }) => {
  const { instance, accounts } = useMsal();
  const allowed = isProposalScheduleAllowed(accounts[0]?.username);
  const estimateNumber = estimate?.estimate_number || '';
  const autoKeyRef = useRef('');
  const chatEndRef = useRef(null);

  const [draft, setDraft] = useState(null);
  const [activities, setActivities] = useState([]);
  const [sourceDocs, setSourceDocs] = useState([]);
  const [narrative, setNarrative] = useState(null);
  const [tab, setTab] = useState('schedule');
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [learnings, setLearnings] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const [buildStatus, setBuildStatus] = useState('');

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3200);
  };

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  /** Poll async generate/refresh until success/failed (full research may take minutes). */
  const waitForRun = useCallback(async (runId, { cancelled } = {}) => {
    const maxMs = 45 * 60 * 1000;
    const started = Date.now();
    let delay = 1500;
    while (Date.now() - started < maxMs) {
      if (cancelled?.()) return null;
      const res = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        `${API}/run/${runId}`
      );
      const run = res.data?.run;
      if (!run) throw new Error('Build run not found');
      if (run.status === 'success') return run;
      if (run.status === 'failed') {
        throw new Error(run.error_message || 'Generation failed');
      }
      const elapsedSec = Math.round((Date.now() - started) / 1000);
      setBuildStatus(`Full research in progress… ${elapsedSec}s (P6 corpus + SharePoint)`);
      await sleep(delay);
      delay = Math.min(delay + 500, 5000);
    }
    throw new Error('Build is still running after 45 minutes — check again shortly');
  }, [instance, accounts]);

  const applyBundle = (data) => {
    setDraft(data.draft || null);
    setActivities(data.activities || []);
    setSourceDocs(data.sourceDocs || []);
    setNarrative(data.narrative || null);
  };

  const loadDraft = useCallback(async (num) => {
    if (!num) return null;
    const res = await authenticatedApiCall(
      instance,
      accounts,
      'GET',
      `${API}/by-estimate/${encodeURIComponent(num)}`
    );
    applyBundle(res.data);
    return res.data;
  }, [instance, accounts]);

  const runGenerate = useCallback(async (num, { refresh = false, quiet = false } = {}) => {
    if (!num) return;
    try {
      setWorking(true);
      setError(null);
      setBuildStatus('Queuing full P6 + SharePoint research…');
      const res = await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        refresh ? `${API}/refresh` : `${API}/generate`,
        { estimateNumber: num }
      );
      const runId = res.data?.runId;
      if (!runId) throw new Error('No build run id returned');
      setBuildStatus(res.data?.message || 'Research started…');
      await waitForRun(runId);
      await loadDraft(num);
      setBuildStatus('');
      if (!quiet) {
        showToast(refresh ? 'New revision ready' : 'Draft ready');
      }
      setTab('schedule');
    } catch (e) {
      const msg = e.response?.data?.error || e.message || 'Generation failed';
      setError(msg);
      setBuildStatus('');
      if (!quiet) showToast(msg, 'error');
    } finally {
      setWorking(false);
    }
  }, [instance, accounts, loadDraft, waitForRun]);

  // Auto-load + auto-create draft when estimate is selected
  useEffect(() => {
    if (!estimateNumber) {
      applyBundle({});
      autoKeyRef.current = '';
      return undefined;
    }

    let cancelled = false;
    const key = estimateNumber;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await loadDraft(estimateNumber);
        if (cancelled) return;
        // First open for this estimate in this mount cycle → create draft if missing
        if (!data?.draft && autoKeyRef.current !== key) {
          autoKeyRef.current = key;
          setLoading(false);
          await runGenerate(estimateNumber, { quiet: true });
          return;
        }
        autoKeyRef.current = key;
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.error || e.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [estimateNumber, loadDraft, runGenerate]);

  const downloadExport = async (kind) => {
    if (!draft?.id) {
      showToast('Draft not ready yet', 'error');
      return;
    }
    try {
      const path = `${API}/${draft.id}/export/${kind}`;
      const res = await authenticatedApiCall(instance, accounts, 'GET', path, null, {
        responseType: 'blob'
      });
      const blob = new Blob([res.data], {
        type: kind === 'xer'
          ? 'text/plain'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const token = String(draft.estimate_number || 'draft').replace(/[^A-Za-z0-9_-]/g, '_');
      a.download = kind === 'xer'
        ? `Proposal_Schedule_${token}.xer`
        : `Proposal_Schedule_${token}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      showToast(`${kind.toUpperCase()} downloaded — import as a new P6 project`);
      await loadDraft(estimateNumber);
    } catch (e) {
      showToast(e.response?.data?.error || e.message || 'Download failed', 'error');
    }
  };

  const totalHours = useMemo(
    () => activities.reduce((s, a) => s + (Number(a.total_hours) || 0), 0),
    [activities]
  );

  const revision = draft?.generation_meta?.revision || null;

  const docsByType = useMemo(() => {
    const m = {};
    for (const d of sourceDocs) {
      m[d.doc_type] = (m[d.doc_type] || 0) + 1;
    }
    return m;
  }, [sourceDocs]);

  const narrativeHtml = useMemo(
    () => markdownToHtml(narrative?.body_markdown || ''),
    [narrative]
  );

  const loadChat = useCallback(async (num) => {
    if (!num || !allowed) return;
    try {
      const [msgRes, learnRes] = await Promise.all([
        authenticatedApiCall(instance, accounts, 'GET', `${API}/chat/${encodeURIComponent(num)}`),
        authenticatedApiCall(
          instance,
          accounts,
          'GET',
          `${API}/learnings?estimateNumber=${encodeURIComponent(num)}`
        )
      ]);
      setChatMessages(msgRes.data?.messages || []);
      setLearnings(learnRes.data?.learnings || []);
    } catch {
      /* ignore — gated API may 403 for unexpected users */
    }
  }, [instance, accounts, allowed]);

  useEffect(() => {
    if (estimateNumber && allowed) loadChat(estimateNumber);
    else {
      setChatMessages([]);
      setLearnings([]);
    }
  }, [estimateNumber, allowed, loadChat]);

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages]);

  const sendChat = async ({ regenerate = false } = {}) => {
    const text = chatInput.trim();
    if (!text || !estimateNumber || chatBusy) return;
    try {
      setChatBusy(true);
      setChatInput('');
      const res = await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        `${API}/chat`,
        { estimateNumber, message: text, regenerate }
      );
      if (res.data?.userMessage) {
        setChatMessages((prev) => [
          ...prev,
          res.data.userMessage,
          res.data.assistantMessage
        ].filter(Boolean));
      } else {
        await loadChat(estimateNumber);
      }
      if (res.data?.learnings?.length) {
        setLearnings((prev) => [...res.data.learnings, ...prev]);
      }
      if (res.data?.runId) {
        setWorking(true);
        setBuildStatus('Full rebuild from coach feedback…');
        try {
          await waitForRun(res.data.runId);
          await loadDraft(estimateNumber);
          showToast('New revision from your feedback');
        } catch (err) {
          showToast(err.message || 'Rebuild failed', 'error');
        } finally {
          setWorking(false);
          setBuildStatus('');
        }
      }
    } catch (e) {
      showToast(e.response?.data?.error || e.message || 'Chat failed', 'error');
    } finally {
      setChatBusy(false);
    }
  };

  if (!allowed) {
    return (
      <div style={{ padding: '48px 24px', textAlign: 'center', color: '#6c757d' }}>
        <div style={{ fontSize: '64px', marginBottom: '16px' }}>🔒</div>
        <h2>Access restricted</h2>
        <p>Proposal Schedule is limited to authorized Project Controls users.</p>
      </div>
    );
  }

  if (!estimateNumber) {
    return (
      <div style={{ padding: '48px 24px', textAlign: 'center', color: '#6c757d' }}>
        <div style={{ fontSize: '64px', marginBottom: '16px' }}>📅</div>
        <h2>No Estimate Selected</h2>
        <p>Pick an estimate from the header dropdown to open its proposal schedule.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '16px 20px', maxWidth: 1680, margin: '0 auto', fontFamily: 'Segoe UI, system-ui, sans-serif' }}>
      {toast && (
        <div style={{
          position: 'fixed', top: 70, right: 20, zIndex: 2000,
          background: toast.type === 'error' ? '#b91c1c' : ACCENT,
          color: '#fff', padding: '10px 16px', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,.2)'
        }}>
          {toast.message}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.45rem', color: '#0f172a' }}>Proposal Schedule</h1>
          <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: '0.92rem', maxWidth: 720 }}>
            Draft for <strong>{estimateNumber}</strong>
            {estimate?.estimate_name ? ` — ${estimate.estimate_name}` : ''}
            {revision ? ` · Rev ${revision}` : ''}.
            Auto-built from historical P6 earliest drafts, Cost Breakdown, and SharePoint docs.
            Use the <strong>Schedule coach</strong> on the right to correct it — feedback trains future drafts.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <ListenButton compact guideId="proposal-schedules-help" title="Proposal Schedule Help" accent={ACCENT} />
          <button type="button" onClick={() => setShowHelp(true)} style={btnGhost}>?</button>
        </div>
      </div>

      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center',
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 14, marginBottom: 14
      }}>
        <button
          type="button"
          onClick={() => runGenerate(estimateNumber, { refresh: true })}
          disabled={working || loading}
          style={btnPrimary}
        >
          {working ? 'Working…' : 'New revision'}
        </button>
        <button type="button" onClick={() => loadDraft(estimateNumber)} disabled={loading || working} style={btnGhost}>
          Reload
        </button>
        <button type="button" onClick={() => downloadExport('excel')} disabled={!draft || working} style={btnPrimary}>
          Download Excel (P6)
        </button>
        <button type="button" onClick={() => downloadExport('xer')} disabled={!draft || working} style={btnPrimary}>
          Download XER
        </button>
      </div>

      {buildStatus && (
        <div style={{
          background: '#ecfeff', color: '#0e7490', border: '1px solid #a5f3fc',
          borderRadius: 8, padding: '10px 12px', marginBottom: 12, fontSize: '0.9rem'
        }}>
          {buildStatus}
          <div style={{ fontSize: '0.78rem', marginTop: 4, opacity: 0.85 }}>
            Full accuracy mode — no shortcuts. Leave this page open; it is OK if this takes several minutes.
          </div>
        </div>
      )}

      {error && (
        <div style={{ background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 12px', marginBottom: 12 }}>
          {error}
        </div>
      )}

      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 12,
        background: '#f0fdfa', border: `1px solid ${ACCENT}33`, borderRadius: 10, padding: '12px 14px'
      }}>
        <Stat label="Estimate" value={estimateNumber} />
        <Stat label="Revision" value={revision ? String(revision) : (loading || working ? '…' : '—')} />
        <Stat label="Status" value={draft?.status || (loading || working ? 'building…' : 'none')} />
        <Stat label="Activities" value={draft ? String(activities.length) : '—'} />
        <Stat label="Hours" value={draft ? String(Math.round(totalHours)) : '—'} />
        <Stat label="Source docs" value={draft ? String(sourceDocs.length) : '—'} />
        <Stat label="Learnings" value={String(learnings.length)} />
        {draft?.needs_cost_breakdown && (
          <span style={{ alignSelf: 'center', color: '#b45309', fontSize: '0.85rem' }}>
            Skeleton — add Cost Breakdown hours, then New revision
          </span>
        )}
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) 380px',
        gap: 14,
        alignItems: 'start'
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 10, borderBottom: '1px solid #e2e8f0' }}>
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                style={{
                  border: 'none', background: 'transparent', padding: '10px 14px', cursor: 'pointer',
                  borderBottom: tab === t.id ? `2px solid ${ACCENT}` : '2px solid transparent',
                  color: tab === t.id ? ACCENT : '#64748b', fontWeight: tab === t.id ? 600 : 500
                }}
              >
                {t.label}
              </button>
            ))}
          </div>

          {(loading || working) && !draft ? (
            <p style={{ color: '#64748b' }}>Building proposal schedule…</p>
          ) : !draft ? (
            <div style={{ background: '#fff', border: '1px dashed #cbd5e1', borderRadius: 10, padding: 28, textAlign: 'center', color: '#64748b' }}>
              No draft yet for <strong>{estimateNumber}</strong>.
              <div style={{ marginTop: 12 }}>
                <button type="button" onClick={() => runGenerate(estimateNumber)} style={btnPrimary}>Build now</button>
              </div>
            </div>
          ) : (
            <>
              {tab === 'schedule' && (
                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'auto' }}>
                  <table style={{ ...tableStyle, margin: 0 }}>
                    <thead>
                      <tr>
                        <th style={thStyle}>WBS</th>
                        <th style={thStyle}>ID</th>
                        <th style={thStyle}>Activity</th>
                        <th style={thStyle}>Discipline</th>
                        <th style={thStyle}>Resource</th>
                        <th style={thStyle}>Hours</th>
                        <th style={thStyle}>Dur</th>
                        <th style={thStyle}>Start</th>
                        <th style={thStyle}>Finish</th>
                        <th style={thStyle}>Pred</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activities.map((a) => (
                        <tr key={a.id || a.activity_id}>
                          <td style={tdStyle}>{a.wbs_name}</td>
                          <td style={{ ...tdStyle, fontFamily: 'ui-monospace, monospace', fontSize: '0.8rem' }}>{a.activity_id}</td>
                          <td style={tdStyle}>{a.activity_name}</td>
                          <td style={tdStyle}>{a.discipline}</td>
                          <td style={tdStyle}>{a.resource_id || a.resource_id_name}</td>
                          <td style={tdStyle}>{Math.round(Number(a.total_hours) || 0)}</td>
                          <td style={tdStyle}>{a.duration}</td>
                          <td style={tdStyle}>{a.start_date ? String(a.start_date).slice(0, 10) : ''}</td>
                          <td style={tdStyle}>{a.finish_date ? String(a.finish_date).slice(0, 10) : ''}</td>
                          <td style={{ ...tdStyle, fontSize: '0.75rem', color: '#64748b' }}>{a.predecessor_ids || ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {tab === 'docs' && (
                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 14 }}>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                    {Object.entries(docsByType).map(([k, v]) => (
                      <span key={k} style={{
                        background: '#f1f5f9', borderRadius: 999, padding: '4px 10px', fontSize: '0.8rem', color: '#334155'
                      }}>
                        {k}: {v}
                      </span>
                    ))}
                  </div>
                  {sourceDocs.length === 0 ? (
                    <p style={{ color: '#64748b' }}>No SharePoint files inventoried for this estimate folder.</p>
                  ) : (
                    <table style={tableStyle}>
                      <thead>
                        <tr>
                          <th style={thStyle}>Type</th>
                          <th style={thStyle}>File</th>
                          <th style={thStyle}>Path</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sourceDocs.map((d) => (
                          <tr key={d.id}>
                            <td style={tdStyle}>{d.doc_type}</td>
                            <td style={tdStyle}>{d.file_name}</td>
                            <td style={{ ...tdStyle, color: '#64748b', fontSize: '0.8rem' }}>{d.folder_path}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {tab === 'narrative' && (
                <div
                  style={{
                    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '8px 22px 22px',
                    fontFamily: 'Segoe UI, system-ui, sans-serif', fontSize: '0.95rem'
                  }}
                  dangerouslySetInnerHTML={{ __html: narrativeHtml || '<p style="color:#64748b">No narrative.</p>' }}
                />
              )}

              {tab === 'exports' && (
                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 18 }}>
                  <p style={{ marginTop: 0, color: '#475569' }}>
                    <strong>Excel</strong> uses the real P6 import layout (TASK / TASKPRED / RSRC / TASKRSRC with two header rows),
                    matching files like <code>26US1035-Current.xlsx</code>.
                    <br />
                    <strong>XER</strong> follows P6 24.12 table structure for import as a <em>new</em> proposal project — never overwrite a working copy.
                  </p>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <button type="button" onClick={() => downloadExport('excel')} style={btnPrimary}>Download Excel (.xlsx)</button>
                    <button type="button" onClick={() => downloadExport('xer')} style={btnPrimary}>Download XER</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <ScheduleCoach
          chatMessages={chatMessages}
          learnings={learnings}
          chatInput={chatInput}
          setChatInput={setChatInput}
          chatBusy={chatBusy || working}
          sendChat={sendChat}
          chatEndRef={chatEndRef}
        />
      </div>

      {showHelp && (
        <div
          role="dialog"
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', zIndex: 3000,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
          }}
          onClick={() => setShowHelp(false)}
        >
          <div
            style={{ background: '#0f172a', color: '#e2e8f0', borderRadius: 12, maxWidth: 560, width: '100%', padding: 20 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>Proposal Schedule</h3>
              <div style={{ display: 'flex', gap: 8 }}>
                <ListenButton compact guideId="proposal-schedules-help" title="Proposal Schedule Help" accent={ACCENT} dark />
                <button type="button" onClick={() => setShowHelp(false)} style={{ ...btnGhost, color: '#fff', borderColor: '#475569' }}>×</button>
              </div>
            </div>
            <HelpWithVideoTour guideId="proposal-schedules-help" title="Proposal Schedule Help" accent={ACCENT}>
            <p style={{ fontSize: '0.92rem', lineHeight: 1.5 }}>
              Select an estimate in the header. The portal builds a draft automatically and keeps revisions
              when you click New revision. The Schedule coach on the right saves your corrections as learnings
              for future estimates. Download Excel or XER for a manual P6 import.
              This page is private to Project Controls (allowlisted users only).
            </p>
            </HelpWithVideoTour>
          </div>
        </div>
      )}
    </div>
  );
};

function Stat({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#64748b' }}>{label}</div>
      <div style={{ fontWeight: 600, color: '#0f172a' }}>{value}</div>
    </div>
  );
}

/** Persistent right-rail chatbot — always visible next to the schedule. */
function ScheduleCoach({
  chatMessages, learnings, chatInput, setChatInput, chatBusy, sendChat, chatEndRef
}) {
  return (
    <aside style={{
      position: 'sticky',
      top: 72,
      background: '#0f172a',
      borderRadius: 12,
      border: '1px solid #1e293b',
      display: 'flex',
      flexDirection: 'column',
      height: 'calc(100vh - 100px)',
      minHeight: 520,
      maxHeight: 820,
      overflow: 'hidden',
      boxShadow: '0 8px 28px rgba(15,23,42,.18)'
    }}>
      <div style={{
        padding: '14px 16px',
        borderBottom: '1px solid #334155',
        background: `linear-gradient(135deg, ${ACCENT} 0%, #115e59 100%)`
      }}>
        <div style={{ color: '#fff', fontWeight: 700, fontSize: '1.05rem' }}>Schedule coach</div>
        <div style={{ color: '#ccfbf1', fontSize: '0.78rem', marginTop: 4, lineHeight: 1.35 }}>
          Tell me what to fix. I save learnings and improve the next revision.
          If instructions conflict, Yousef’s rules always win over Behzad’s.
        </div>
      </div>

      <div style={{
        flex: 1, overflow: 'auto', padding: 12,
        display: 'flex', flexDirection: 'column', gap: 10, background: '#0f172a'
      }}>
        {chatMessages.length === 0 && (
          <div style={{
            background: '#1e293b', borderRadius: 10, padding: 12,
            color: '#94a3b8', fontSize: '0.85rem', lineHeight: 1.5
          }}>
            Try:
            <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
              <li>Always put structural erection before piping field work</li>
              <li>Make engineering longer when drawings exist</li>
              <li>For this estimate only, overlap fab and field</li>
            </ul>
          </div>
        )}
        {chatMessages.map((m) => (
          <div
            key={m.id}
            style={{
              alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
              maxWidth: '92%',
              background: m.role === 'user' ? ACCENT : '#1e293b',
              color: m.role === 'user' ? '#fff' : '#e2e8f0',
              borderRadius: 10,
              padding: '8px 11px',
              fontSize: '0.86rem',
              whiteSpace: 'pre-wrap',
              lineHeight: 1.45
            }}
          >
            <div style={{
              fontSize: '0.68rem',
              opacity: 0.75,
              marginBottom: 3,
              color: m.role === 'user' ? '#ccfbf1' : '#94a3b8'
            }}>
              {m.role === 'user' ? (m.user_email || 'You') : 'Coach'}
            </div>
            {m.content}
          </div>
        ))}
        <div ref={chatEndRef} />
      </div>

      {learnings.length > 0 && (
        <div style={{
          borderTop: '1px solid #334155', padding: '8px 12px',
          maxHeight: 110, overflow: 'auto', background: '#111827'
        }}>
          <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginBottom: 4, fontWeight: 600 }}>
            SAVED LEARNINGS ({learnings.length})
          </div>
          {learnings.slice(0, 8).map((l) => (
            <div key={l.id} style={{ fontSize: '0.75rem', color: '#cbd5e1', marginBottom: 4, lineHeight: 1.35 }}>
              <span style={{ color: l.scope === 'global' ? '#5eead4' : '#fcd34d' }}>
                [{l.scope}/{l.category}]
              </span>{' '}
              {l.instruction}
            </div>
          ))}
        </div>
      )}

      <div style={{ borderTop: '1px solid #334155', padding: 10, background: '#111827' }}>
        <textarea
          value={chatInput}
          onChange={(e) => setChatInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              sendChat();
            }
          }}
          placeholder="What should change on this schedule?"
          rows={2}
          disabled={chatBusy}
          style={{
            width: '100%', boxSizing: 'border-box', resize: 'none',
            borderRadius: 8, border: '1px solid #475569', background: '#0f172a',
            color: '#e2e8f0', padding: '8px 10px', fontFamily: 'inherit', fontSize: '0.88rem',
            marginBottom: 8
          }}
        />
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            type="button"
            onClick={() => sendChat()}
            disabled={chatBusy || !chatInput.trim()}
            style={{ ...btnPrimary, flex: 1, opacity: chatBusy || !chatInput.trim() ? 0.55 : 1 }}
          >
            {chatBusy ? 'Thinking…' : 'Send'}
          </button>
          <button
            type="button"
            onClick={() => sendChat({ regenerate: true })}
            disabled={chatBusy || !chatInput.trim()}
            style={{
              ...btnGhost,
              flex: 1,
              background: '#1e293b',
              color: '#e2e8f0',
              borderColor: '#475569',
              opacity: chatBusy || !chatInput.trim() ? 0.55 : 1
            }}
            title="Save feedback and rebuild a new revision"
          >
            Send + apply
          </button>
        </div>
      </div>
    </aside>
  );
}

const btnPrimary = {
  background: ACCENT, color: '#fff', border: 'none', borderRadius: 8,
  padding: '8px 14px', cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem'
};
const btnGhost = {
  background: '#fff', color: '#334155', border: '1px solid #cbd5e1', borderRadius: 8,
  padding: '8px 14px', cursor: 'pointer', fontWeight: 500, fontSize: '0.88rem'
};
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' };
const thStyle = {
  textAlign: 'left', padding: '8px 10px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0',
  color: '#475569', fontWeight: 600, whiteSpace: 'nowrap'
};
const tdStyle = { padding: '7px 10px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' };

export default ProposalSchedules;
